import dayjs from 'dayjs';
import { memberDetailPath, memberDetailState } from '../../lib/memberRoutes';

/**
 * 회원 줄 버튼이 **어디로 가는가** (2026-09-01).
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 원칙 — 실재하는 화면으로만, 발명 금지
 * ─────────────────────────────────────────────────────────────
 * 라우트가 있다는 것과 그 화면이 그 일을 한다는 것은 다르다. QA34 에서
 * 세팅 카드의 「강사 현황에서 보기」를 없앤 이유가 그것이었다 — 라우트는
 * 실재했지만 신규 센터에서는 그 탭이 비어 있어서, 누르면 빈 화면이 나왔다.
 *
 * 그래서 목적지마다 **그 화면이 실제로 그 일을 하는 증거**를 적어 둔다.
 * 없으면 버튼을 만들지 않고 안내 문구로 둔다. 눌러도 아무 일이 없는
 * 버튼보다 「여기서는 못 한다」가 낫다.
 */

export type MemberNeed = 'PASS' | 'APP' | 'BOOKING' | 'JOURNAL' | 'SUGGEST_PASS';

export type MemberActionTarget = {
    memberId: string;
    memberName: string;
    coachId: string;
    coachName: string;
};

/** 이동할 곳 — `null` 이면 갈 데가 없다는 뜻이고, 화면은 버튼 대신 안내를 그린다 */
export type MemberNav = { to: string; state: Record<string, unknown> } | null;

/**
 * 갈 데가 없는 버튼의 **안내 문구**.
 *
 * 🔴 「없어서 안 만들었다」가 아니라 **원장님이 지금 할 수 있는 일**을 적는다.
 *    빈 안내는 화면을 못 믿게 만들 뿐이다.
 */
export const NEED_HINT: Partial<Record<MemberNeed, string>> = {
    /*
     * 회원 앱 초대를 **보내는 화면이 이 제품에 없다.** 전수 검색해도
     * 초대코드·초대 문자 발송 경로가 없고, 공유 수강권의 `invitees` 는
     * 전혀 다른 개념이다(수강권을 나눠 쓰는 회원 초대).
     * 실제 절차는 회원이 앱에서 센터 코드를 넣는 것이라 그것을 적는다.
     */
    APP: '회원이 라포 앱을 설치하고 센터 초대코드를 입력하면 연동됩니다 · 여기서 보내는 기능은 없습니다',
    /*
     * 일지는 **강사가 쓰는 것**이고, 원장이 요청을 보내는 플로우가 이 제품에
     * 없다. 강사 현황에 일지 작성률 컬럼은 있지만 그건 월 단위 지표라
     * 「이 회원의 이 수업」과 이어지지 않는다 — 보내면 엉뚱한 화면이다.
     */
    JOURNAL: '일지는 강사가 앱에서 작성합니다 · 여기서 요청을 보내는 기능은 없습니다',
};

/**
 * 목적지 표.
 *
 * | 버튼 | 목적지 | 그 화면이 그 일을 한다는 증거 |
 * |---|---|---|
 * | 수강권 등록 | `/member/:memberId` | 「통합 회원 상세」(`PublicMemberDetailPage`)의 **「+ 수강권 발급」**·**「수강권 발급하기」** 버튼이 `ModalMembershipCreate` 를 연다 |
 * | 수강권 제안 | 〃 | 신규기회도 등록 절차는 같다. 다른 것은 **톤**뿐이라 목적지를 가르지 않는다 |
 * | 스케줄 잡기 | `/schedule/create` | `CreateSchedulePage`. 같은 인자 형태를 `MemberMemberShipComponents.onClickMoveToClassRegister` 가 이미 쓴다 |
 * | 앱 초대 | — | 발송 화면이 없다 (`NEED_HINT.APP`) |
 * | 일지 요청 | — | 요청 플로우가 없다 (`NEED_HINT.JOURNAL`) |
 */
export const memberNav = (need: MemberNeed, t: MemberActionTarget): MemberNav => {
    switch (need) {
        case 'PASS':
        case 'SUGGEST_PASS':
            /*
             * 🔴 **회원 목록과 같은 함수를 쓴다** (`lib/memberRoutes`).
             *
             * 예전에는 여기에 `/member/detail` 을 적어 뒀는데 그건 **구 화면**
             * 이었다. 라우터에 살아 있어서 눌리기는 했지만, 지금 원장들이 쓰는
             * 화면이 아니라 실사용에서 「예전 화면이 뜬다」는 지적이 나왔다.
             *
             * 라우터 정의만 보면 둘 다 실재해서 구별이 안 된다. 그래서 원장이
             * 매일 누르는 자리(회원 목록 행 클릭)와 **같은 정의**를 쓰게 했다 —
             * 그게 죽으면 즉시 드러나므로 이 링크도 같이 살아 있다.
             */
            return { to: memberDetailPath(t.memberId), state: memberDetailState(t.memberId) };

        case 'BOOKING':
            /*
             * 수업 등록 화면. 인자 형태는 회원 상세의 「수업 등록 바로가기」와
             * **같은 것을 그대로** 쓴다 — 거기서 검증된 조합이라 화면이 바로 뜬다.
             *
             * ⚠️ `type` 이 없으면 그 화면은 아무것도 못 그린다. `oneSchedule` 은
             *    피처플래그 가드에도 안 걸린다(`newOpportunitySchedule` 만 걸린다).
             * ⚠️ `membershipId` 는 넣지 않는다 — 이 카드가 뜨는 회원은 수강권이
             *    있지만 예약이 없는 사람이고, 어느 수강권으로 잡을지는 그 화면에서
             *    고른다. 여기서 골라 주면 틀린 수강권이 차감될 수 있다.
             */
            return {
                to: '/schedule/create',
                state: {
                    type: 'oneSchedule',
                    selectedSchedule: {
                        memberId: t.memberId,
                        coachId: t.coachId,
                        coachName: t.coachName,
                        scheduleStartTime: dayjs().format('YYYY-MM-DD HH:mm'),
                    },
                    calendarType: 'daily',
                    calendarSelectedDay: dayjs().format('YYYY-MM-DD'),
                    calendarScrollY: 0,
                    /* 돌아올 곳 — 아침 브리핑을 보다가 갔으므로 거기로 돌아온다 */
                    returnPath: '/opsBriefing/today',
                },
            };

        default:
            return null;
    }
};
